import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

type Hint = { text: string; x: number; top: number; bottom: number };

const SHOW_DELAY_MS = 250;

/**
 * One tooltip for every element carrying `data-hint`, anywhere on the page (dialogs included).
 *
 * For dense surfaces such as the batch grid, where a tooltip component per cell would mean
 * thousands of them, and where the browser's own `title` tooltip is too slow and does not show in
 * embedded browser panels at all. Mount once per page; mark elements with `data-hint="…"`.
 * Line breaks in the text are kept. Shows on hover and on keyboard focus.
 */
export function HintLayer() {
  const [hint, setHint] = useState<Hint | null>(null);

  useEffect(() => {
    let timer: number | undefined;
    let current: Element | null = null;
    // The element a file was just dropped on. Its hint stays quiet until the pointer has left it.
    let dropped: Element | null = null;

    const hide = () => {
      window.clearTimeout(timer);
      current = null;
      setHint(null);
    };
    const showFor = (target: EventTarget | null, delay: number) => {
      const el = target instanceof Element ? target.closest('[data-hint]') : null;
      if (el === current) return;
      hide();
      if (el && el === dropped) return;
      dropped = null;
      const text = el?.getAttribute('data-hint')?.trim();
      if (!el || !text) return;
      current = el;
      timer = window.setTimeout(() => {
        const rect = el.getBoundingClientRect();
        setHint({ text, x: rect.left + rect.width / 2, top: rect.top, bottom: rect.bottom });
      }, delay);
    };

    const onOver = (e: MouseEvent) => showFor(e.target, SHOW_DELAY_MS);
    const onFocus = (e: FocusEvent) => {
      // Keyboard focus only: a click also focuses, and the pointer is already handling that case.
      if (e.target instanceof Element && e.target.matches(':focus-visible')) showFor(e.target, 0);
    };
    const onDrag = (e: DragEvent) => {
      hide();
      dropped = e.target instanceof Element ? e.target.closest('[data-hint]') : null;
    };
    document.addEventListener('dragover', onDrag);
    document.addEventListener('drop', onDrag);
    document.addEventListener('mouseover', onOver);
    document.addEventListener('focusin', onFocus);
    document.addEventListener('focusout', hide);
    document.addEventListener('mousedown', hide);
    document.addEventListener('keydown', hide);
    window.addEventListener('scroll', hide, true);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('dragover', onDrag);
      document.removeEventListener('drop', onDrag);
      document.removeEventListener('mouseover', onOver);
      document.removeEventListener('focusin', onFocus);
      document.removeEventListener('focusout', hide);
      document.removeEventListener('mousedown', hide);
      document.removeEventListener('keydown', hide);
      window.removeEventListener('scroll', hide, true);
    };
  }, []);

  if (!hint) return null;
  // Above the element, unless that would run off the top of the window.
  const above = hint.top > 72;
  return createPortal(
    <div
      role="tooltip"
      className="pointer-events-none fixed z-[200] max-w-72 rounded-md bg-foreground px-3 py-1.5 text-xs whitespace-pre-line [overflow-wrap:anywhere] text-background shadow-md"
      style={{
        left: Math.min(Math.max(hint.x, 152), window.innerWidth - 152),
        top: above ? hint.top - 6 : hint.bottom + 6,
        transform: `translate(-50%, ${above ? '-100%' : '0'})`,
      }}
    >
      {hint.text}
    </div>,
    document.body
  );
}
